import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import InfoBubble from "../components/InfoBubble";
import SupportChoiceBar from "../components/SupportChoiceBar";
import useGuidance from "../hooks/useGuidance";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  TICKET_STATUS_STYLES as STATUS_STYLES,
  TICKET_PRIORITY_STYLES as PRIORITY_STYLES,
  TICKET_CATEGORY_LABELS as CATEGORY_LABELS,
} from "../constants/registry";
import {
  Ticket, Plus, Users, Filter, Search, ChevronRight, Loader2,
  AlertTriangle, Clock, CheckCircle, MessageSquare, User, X,
  Send, Eye, EyeOff, ArrowLeft, RefreshCw, UserPlus
} from "lucide-react";

const SupportTicketsPage = () => {
  const { isAdmin, isAuditor, user: authUser } = useAuth();
  const { guidance } = useGuidance();
  const { t } = useTranslation('platform');
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "general", priority: "medium", channel: "manual" });
  const [msgText, setMsgText] = useState("");
  const [msgVisibility, setMsgVisibility] = useState("external");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (priorityFilter !== "all") params.set("priority", priorityFilter);
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      const [tRes, sRes] = await Promise.all([
        fetch(`${API}/crm/support-tickets?${params}`, { credentials: "include" }),
        fetch(`${API}/crm/support-tickets/summary`, { credentials: "include" }),
      ]);
      if (tRes.ok) {
        const data = await tRes.json();
        // API returns {tickets: [...], total: N}
        setTickets(data.tickets || []);
      }
      if (sRes.ok) setSummary(await sRes.json());
    } catch (err) {
      console.error("Tickets fetch failed:", err);
    }
    setLoading(false);
  }, [statusFilter, priorityFilter, categoryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadTicketDetail = async (ticketId) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets/${ticketId}`, { credentials: "include" });
      if (res.ok) setSelectedTicket(await res.json());
    } catch (err) {
      toast.error("Failed to load ticket");
    }
    setDetailLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success("Ticket created");
        setShowCreate(false);
        setForm({ subject: "", description: "", category: "general", priority: "medium", channel: "manual" });
        fetchData();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Failed to create ticket");
      }
    } catch (err) { toast.error("Failed to create ticket"); }
    setCreating(false);
  };

  const handleSendMessage = async () => {
    if (!msgText.trim() || !selectedTicket) return;
    setSendingMsg(true);
    try {
      const res = await fetch(`${API}/crm/support-tickets/${selectedTicket.ticket_id}/messages`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ content: msgText, visibility: msgVisibility }),
      });
      if (res.ok) {
        setMsgText("");
        loadTicketDetail(selectedTicket.ticket_id);
      }
    } catch (err) { toast.error("Failed to send message"); }
    setSendingMsg(false);
  };

  const handleAssign = async () => {
    if (!assignUserId.trim() || !selectedTicket) return;
    try {
      const res = await fetch(`${API}/crm/support-tickets/${selectedTicket.ticket_id}/assign`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ assigned_to_user_id: assignUserId }),
      });
      if (res.ok) {
        toast.success("Ticket assigned");
        setAssignUserId("");
        loadTicketDetail(selectedTicket.ticket_id);
        fetchData();
      }
    } catch (err) { toast.error("Failed to assign"); }
  };

  const handleResolve = async () => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API}/crm/support-tickets/${selectedTicket.ticket_id}/resolve`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) {
        toast.success("Ticket resolved");
        loadTicketDetail(selectedTicket.ticket_id);
        fetchData();
      }
    } catch (err) { toast.error("Failed to resolve"); }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      const res = await fetch(`${API}/crm/support-tickets/${selectedTicket.ticket_id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        loadTicketDetail(selectedTicket.ticket_id);
        fetchData();
      }
    } catch (err) { toast.error("Failed to update status"); }
  };

  const filtered = tickets.filter(t =>
    t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.ticket_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin && !isAuditor) {
    return <QMSLayout title={t('support.ticketManagement')}><div className="text-center py-12 text-[#8B95A5]">Access restricted to admin and auditor roles.</div></QMSLayout>;
  }

  return (
    <QMSLayout title={t('support.ticketManagement')}>
      <div className="max-w-7xl mx-auto" data-testid="support-tickets-page">
        {guidance?.info_hint && <InfoBubble text={guidance.info_hint} title={t('support.ticketManagement')} />}
        {/* KPI Strip */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="support-summary">
            {[
              { label: t('support.openTickets'), value: summary.open, color: "#3B82F6" },
              { label: "In Progress", value: summary.in_progress, color: "#EAB308" },
              { label: "Waiting", value: summary.waiting, color: "#A78BFA" },
              { label: "Critical", value: summary.critical_open, color: "#EF4444" },
              { label: "Unassigned", value: summary.unassigned, color: "#F97316" },
              { label: "Resolved", value: summary.resolved, color: "#10B981" },
            ].map(({ label, value, color }) => (
              <div key={label} className="p-3 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27]">
                <div className="text-[#8B95A5] text-[10px] mb-1">{label}</div>
                <div className="text-xl font-bold font-mono" style={{ color }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-4">
          {/* Left: Ticket List */}
          <div className={`${selectedTicket ? "w-1/2" : "w-full"} transition-all`}>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="relative flex-1 min-w-[180px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B95A5]" />
                <input
                  type="text" placeholder={t('support.searchTickets')}
                  value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
                  data-testid="support-search"
                />
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs" data-testid="support-status-filter">
                <option value="all">{t('support.allStatuses')}</option>
                {Object.entries(STATUS_STYLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs" data-testid="support-priority-filter">
                <option value="all">{t('support.allPriorities')}</option>
                <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
              </select>
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-xs" data-testid="support-category-filter">
                <option value="all">{t('support.allCategories')}</option>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] transition-colors" data-testid="support-create-btn">
                <Plus size={14} /> New Ticket
              </button>
            </div>

            {/* Ticket List */}
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={24} /></div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-[#8B95A5]">
                <Ticket size={32} className="mx-auto mb-2 opacity-40" />
                <p>No tickets found</p>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="support-ticket-list">
                {filtered.map(tkt => {
                  const st = STATUS_STYLES[tkt.status] || STATUS_STYLES.open;
                  const pr = PRIORITY_STYLES[tkt.priority] || PRIORITY_STYLES.medium;
                  const isSelected = selectedTicket?.ticket_id === tkt.ticket_id;
                  return (
                    <button
                      key={tkt.ticket_id}
                      onClick={() => loadTicketDetail(tkt.ticket_id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${isSelected ? "border-[#36A9E1] bg-[rgba(54,169,225,0.06)]" : "border-[rgba(255,255,255,0.08)] bg-[#1A1D27] hover:border-[rgba(255,255,255,0.15)]"}`}
                      data-testid={`support-ticket-${tkt.ticket_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-white text-sm font-medium truncate">{tkt.subject}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${pr.bg} ${pr.text}`}>{tkt.priority}</span>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-[#8B95A5]">
                            <span className="font-mono">{tkt.ticket_id}</span>
                            <span>{CATEGORY_LABELS[tkt.category] || tkt.category}</span>
                            <span>{tkt.requester_email}</span>
                            {tkt.message_count > 0 && <span className="flex items-center gap-0.5"><MessageSquare size={8} />{tkt.message_count}</span>}
                          </div>
                        </div>
                        <ChevronRight size={14} className="text-[#8B95A5] shrink-0" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: Ticket Detail Panel */}
          {selectedTicket && (
            <div className="w-1/2 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27] overflow-hidden flex flex-col" style={{ maxHeight: "calc(100vh - 140px)" }} data-testid="support-detail-panel">
              {detailLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#36A9E1]" size={20} /></div>
              ) : (
                <>
                  {/* Detail Header */}
                  <div className="p-4 border-b border-[rgba(255,255,255,0.06)]">
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={() => setSelectedTicket(null)} className="text-[#8B95A5] hover:text-white">
                        <X size={16} />
                      </button>
                      <span className="text-[#8B95A5] text-[10px] font-mono">{selectedTicket.ticket_id}</span>
                    </div>
                    <h3 className="text-white font-semibold text-sm mb-2" data-testid="support-detail-subject">{selectedTicket.subject}</h3>
                    <p className="text-[#8B95A5] text-xs mb-3">{selectedTicket.description}</p>
                    <div className="flex flex-wrap gap-2 text-[10px]">
                      {(() => { const st = STATUS_STYLES[selectedTicket.status]; return <span className={`px-2 py-0.5 rounded font-medium ${st?.bg} ${st?.text}`}>{st?.label}</span>; })()}
                      {(() => { const pr = PRIORITY_STYLES[selectedTicket.priority]; return <span className={`px-2 py-0.5 rounded font-medium ${pr?.bg} ${pr?.text}`}>{selectedTicket.priority}</span>; })()}
                      <span className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#8B95A5]">{CATEGORY_LABELS[selectedTicket.category]}</span>
                      <span className="px-2 py-0.5 rounded bg-[rgba(255,255,255,0.04)] text-[#8B95A5]">{selectedTicket.channel}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-[#8B95A5]">
                      <span>By: {selectedTicket.requester_email}</span>
                      {selectedTicket.assigned_to_user_id && <span>Assigned: {selectedTicket.assigned_to_user_id}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="p-3 border-b border-[rgba(255,255,255,0.06)] flex flex-wrap gap-2">
                    {/* Status buttons */}
                    {["open", "in_progress", "waiting"].filter(s => s !== selectedTicket.status).map(s => (
                      <button key={s} onClick={() => handleStatusChange(s)} className={`px-2 py-1 rounded text-[10px] font-medium ${STATUS_STYLES[s].bg} ${STATUS_STYLES[s].text}`}>
                        {STATUS_STYLES[s].label}
                      </button>
                    ))}
                    {selectedTicket.status !== "resolved" && selectedTicket.status !== "closed" && (
                      <button onClick={handleResolve} className="px-2 py-1 rounded text-[10px] font-medium bg-emerald-500/10 text-emerald-400" data-testid="support-resolve-btn">
                        <CheckCircle size={10} className="inline mr-1" />Resolve
                      </button>
                    )}
                    {/* Assign */}
                    <div className="flex items-center gap-1 ml-auto">
                      <input
                        value={assignUserId} onChange={e => setAssignUserId(e.target.value)}
                        placeholder="user_id to assign"
                        className="px-2 py-1 rounded bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-[10px] w-32"
                        data-testid="support-assign-input"
                      />
                      <button onClick={handleAssign} className="px-2 py-1 rounded text-[10px] font-medium bg-[rgba(54,169,225,0.1)] text-[#36A9E1]" data-testid="support-assign-btn">
                        <UserPlus size={10} className="inline mr-0.5" />Assign
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-messages">
                    {(selectedTicket.messages || []).length === 0 ? (
                      <div className="text-center text-[#8B95A5] text-xs py-6">No messages yet</div>
                    ) : (
                      (selectedTicket.messages || []).map(msg => (
                        <div key={msg.message_id} className={`p-3 rounded-lg text-xs ${msg.actor_type === "internal" ? "bg-[rgba(54,169,225,0.06)] border border-[rgba(54,169,225,0.1)]" : "bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]"}`} data-testid={`support-msg-${msg.message_id}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">{msg.actor_email}</span>
                            <span className={`px-1 py-0.5 rounded text-[9px] ${msg.visibility === "internal" ? "bg-amber-500/10 text-amber-400" : "bg-emerald-500/10 text-emerald-400"}`}>
                              {msg.visibility === "internal" ? <><EyeOff size={8} className="inline mr-0.5" />Internal</> : <><Eye size={8} className="inline mr-0.5" />External</>}
                            </span>
                            <span className="text-[#8B95A5] text-[9px] ml-auto">{new Date(msg.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-[#B8C7D9] whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Compose */}
                  <div className="p-3 border-t border-[rgba(255,255,255,0.06)]">
                    <div className="flex gap-2 mb-2">
                      <button onClick={() => setMsgVisibility("external")} className={`px-2 py-1 rounded text-[10px] font-medium ${msgVisibility === "external" ? "bg-emerald-500/15 text-emerald-400" : "bg-[rgba(255,255,255,0.04)] text-[#8B95A5]"}`} data-testid="support-msg-external-btn">
                        <Eye size={10} className="inline mr-0.5" />External
                      </button>
                      <button onClick={() => setMsgVisibility("internal")} className={`px-2 py-1 rounded text-[10px] font-medium ${msgVisibility === "internal" ? "bg-amber-500/15 text-amber-400" : "bg-[rgba(255,255,255,0.04)] text-[#8B95A5]"}`} data-testid="support-msg-internal-btn">
                        <EyeOff size={10} className="inline mr-0.5" />Internal
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={msgText} onChange={e => setMsgText(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && handleSendMessage()}
                        placeholder="Type a message..."
                        className="flex-1 px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white text-sm placeholder:text-[#8B95A5] focus:border-[#36A9E1] focus:outline-none"
                        data-testid="support-msg-input"
                      />
                      <button onClick={handleSendMessage} disabled={sendingMsg || !msgText.trim()} className="px-3 py-2 rounded-md bg-[#36A9E1] text-white disabled:opacity-50" data-testid="support-msg-send">
                        {sendingMsg ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="support-create-modal">
            <form onSubmit={handleCreate} className="bg-[#1A1D27] border border-[rgba(255,255,255,0.1)] rounded-lg p-6 w-full max-w-lg shadow-2xl">
              <h2 className="text-white font-semibold text-lg mb-4 font-['Syne']">New Support Ticket</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">Subject *</label>
                  <input value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none" required data-testid="support-form-subject" />
                </div>
                <div>
                  <label className="text-[#8B95A5] text-xs mb-1 block">Description *</label>
                  <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} className="w-full px-3 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-sm focus:border-[#36A9E1] focus:outline-none resize-none" required data-testid="support-form-description" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">Category</label>
                    <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-xs" data-testid="support-form-category">
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">Priority</label>
                    <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="w-full px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-xs" data-testid="support-form-priority">
                      <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[#8B95A5] text-xs mb-1 block">Channel</label>
                    <select value={form.channel} onChange={e => setForm(p => ({ ...p, channel: e.target.value }))} className="w-full px-2 py-2 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.1)] text-white text-xs" data-testid="support-form-channel">
                      <option value="manual">Manual</option><option value="ai_chat">AI Chat</option><option value="email">Email</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-md text-sm text-[#8B95A5] hover:text-white transition-colors">Cancel</button>
                <button type="submit" disabled={creating} className="flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] disabled:opacity-50 transition-colors" data-testid="support-form-submit">
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Create Ticket
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default SupportTicketsPage;
